import React, { useEffect, useState } from "react";
import KSLogo from "../logo/KSLogo";

/**
 * Loader
 * Full-screen cyber boot sequence shown before the portfolio loads.
 *  - Glitch / jitter KS monogram
 *  - Animated horizontal scan line + grid background
 *  - Boot-up progress bar with rotating status text
 *  - Smoothly fades out and unmounts when `onFinish` is invoked
 */
const STATUS_LINES = [
  "INITIALIZING SECURE SHELL...",
  "DECRYPTING IDENTITY PACKETS...",
  "INJECTING DESIGN PAYLOAD...",
  "RUNNING OWASP TOP 10 CHECKS...",
  "SPINNING UP PORTFOLIO...",
];

const Loader = ({ onFinish, duration = 2600 }) => {
  const [progress, setProgress] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setProgress(pct);
      if (pct < 100) {
        raf = requestAnimationFrame(tick);
      } else {
        setFading(true);
        setTimeout(() => onFinish && onFinish(), 650);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [duration, onFinish]);

  useEffect(() => {
    const id = setInterval(() => {
      setStatusIdx((i) => (i + 1) % STATUS_LINES.length);
    }, duration / STATUS_LINES.length);
    return () => clearInterval(id);
  }, [duration]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-bodyColor overflow-hidden transition-opacity duration-700 ${
        fading ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      aria-hidden={fading}
    >
      {/* Cyber grid + radial vignette */}
      <div className="loader-grid absolute inset-0 opacity-40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,1,79,0.18),_transparent_60%)]" />
      {/* Moving scan line */}
      <div className="loader-scanline absolute left-0 right-0 h-px bg-designColor/70 shadow-[0_0_18px_4px_rgba(255,1,79,0.55)]" />

      {/* Logo with glitch */}
      <div className="relative flex items-center justify-center">
        {/* Rotating dashed rings */}
        <div className="absolute w-[260px] h-[260px] rounded-full border border-designColor/30 border-dashed animate-[spin_8s_linear_infinite]" />
        <div className="absolute w-[200px] h-[200px] rounded-full border border-white/10 border-dashed animate-[spin_6s_linear_infinite_reverse]" />

        <div className="loader-glitch relative">
          <div className="loader-glitch-layer loader-glitch-layer--base">
            <KSLogo size={140} glow />
          </div>
          <div className="loader-glitch-layer loader-glitch-layer--cyan">
            <KSLogo size={140} />
          </div>
          <div className="loader-glitch-layer loader-glitch-layer--pink">
            <KSLogo size={140} />
          </div>
        </div>
      </div>

      {/* Glitching name */}
      <h1
        className="mt-12 text-3xl sml:text-4xl font-bold tracking-[0.4em] text-white loader-text-glitch"
        data-text="KUSHAL SHARMA"
      >
        KUSHAL SHARMA
      </h1>
      <p className="mt-3 text-xs sml:text-sm tracking-[0.5em] text-designColor">
        {"// SECURE_BOOT_SEQUENCE"}
      </p>

      {/* Progress + status */}
      <div className="mt-10 w-[260px] sml:w-[340px]">
        <div className="flex justify-between text-[10px] tracking-[0.3em] text-gray-400 mb-2">
          <span>LOADING</span>
          <span>{Math.floor(progress)}%</span>
        </div>
        <div className="h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-designColor via-pink-400 to-designColor shadow-[0_0_10px_rgba(255,1,79,0.7)] transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-4 text-center text-[11px] tracking-[0.2em] text-gray-300/80 min-h-[16px]">
          {STATUS_LINES[statusIdx]}
        </p>
      </div>

      {/* Corner brackets */}
      <CornerBracket position="top-left" />
      <CornerBracket position="top-right" />
      <CornerBracket position="bottom-left" />
      <CornerBracket position="bottom-right" />
    </div>
  );
};

const CornerBracket = ({ position }) => {
  const map = {
    "top-left": "top-6 left-6 border-t-2 border-l-2",
    "top-right": "top-6 right-6 border-t-2 border-r-2",
    "bottom-left": "bottom-6 left-6 border-b-2 border-l-2",
    "bottom-right": "bottom-6 right-6 border-b-2 border-r-2",
  };
  return (
    <span
      className={`absolute w-6 h-6 border-designColor/70 ${map[position]}`}
    />
  );
};

export default Loader;
